import { Component, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ResourceRS, ResourcesRS } from 'src/app/core/models/resources/resources-response';

/** Resultado que devuelve el modal al cerrarse. */
export interface MediaPickerResult {
  /** 'device' = el usuario eligio un fichero de su equipo. 'library' = eligio uno ya subido. */
  source: 'device' | 'library';
  /** Solo en device. Puede traer varios si el hueco admite multiples ficheros. */
  files?: File[];
  /** Solo en 'library'. */
  resource?: ResourcesRS;
}

/**
 * Pregunta al usuario de donde quiere sacar la imagen: de su biblioteca o de su equipo.
 *
 * Este modal NO sube nada ni valida nada: se limita a devolver la eleccion. Quien lo abre
 * es el que decide que hacer, reutilizando su propio flujo de subida. Asi no se duplica la
 * validacion de ficheros.
 *
 * La biblioteca va primera y es la pestana por defecto: es lo que el usuario necesita ver
 * para poder decidir, y esconderla detras de una pestana la hacia invisible.
 */
@Component({
  selector: 'app-media-picker-modal',
  templateUrl: './media-picker-modal.component.html',
  styleUrls: ['./media-picker-modal.component.scss'],
})
export class MediaPickerModalComponent {

  /** Tienda cuya biblioteca se muestra. Null = todas las del usuario. */
  @Input() productDialogPilot = false;
  @Input() siteId: string | null = null;

  /** True si el hueco de destino admite varios ficheros. Lo fija quien abre el modal. */
  @Input() multiple = false;

  /** Tipos admitidos, los de la instancia que abre el modal. No debe ser una lista fija. */
  @Input() accept = "image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.rtf";

  /**
   * Restringe tambien la pestana "biblioteca" a un solo tipo. El `accept` de arriba solo
   * limita el input del equipo; sin esto un hueco de solo imagen aceptaria un PDF ya subido.
   */
  @Input() forcedType: ResourceRS.TypeEnum | null = null;

  activeTab: 'device' | 'library' = 'library';

  /** Resaltado de la zona mientras se arrastra un fichero por encima. */
  isDragging = false;

  /** Motivo por el que se ha rechazado lo que se acaba de soltar. Vacio = nada que decir. */
  dropError = '';

  constructor(public activeModal: NgbActiveModal, private translate: TranslateService) {}

  setTab(tab: 'device' | 'library'): void {
    this.activeTab = tab;
    this.isDragging = false;
    this.dropError = '';
  }

  onDeviceFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    // Se limpia el input para que elegir dos veces el mismo fichero vuelva a disparar el change.
    input.value = '';
    this.emitFiles(files);
  }

  onDragOver(event: DragEvent): void {
    // Sin preventDefault el navegador abre el fichero en una pestana nueva.
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    this.dropError = '';
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : [];
    this.emitFiles(files);
  }

  /**
   * Cierra el modal con los ficheros elegidos, vengan del explorador o de arrastrarlos.
   *
   * El `accept` del input lo aplica el navegador, pero al soltar no lo aplica nadie: sin este
   * filtro se podia colar un PDF en un hueco de solo imagen. La validacion de tamano y demas
   * la sigue haciendo quien abrio el modal, aqui solo se descarta lo que ni siquiera encaja.
   */
  private emitFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    const accepted = files.filter(file => this.matchesAccept(file));
    if (accepted.length === 0) {
      this.dropError = this.translate.instant('ml_picker_type_rejected');
      return;
    }

    const payload = this.multiple ? accepted : [accepted[0]];
    this.activeModal.close({ source: 'device', files: payload } as MediaPickerResult);
  }

  /** Comprueba un fichero contra el `accept`, que usa las formas `image/*`, `image/png` y `.pdf`. */
  private matchesAccept(file: File): boolean {
    const patterns = (this.accept || '')
      .split(',')
      .map(pattern => pattern.trim().toLowerCase())
      .filter(pattern => pattern !== '');

    if (patterns.length === 0) {
      return true;
    }

    const mime = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    return patterns.some(pattern => {
      if (pattern === '*/*') {
        return true;
      }
      if (pattern.startsWith('.')) {
        return name.endsWith(pattern);
      }
      if (pattern.endsWith('/*')) {
        return mime.startsWith(pattern.slice(0, -1));
      }
      return mime === pattern;
    });
  }

  onLibrarySelected(resource: ResourcesRS): void {
    if (!resource || !resource.url) {
      return;
    }
    this.activeModal.close({ source: 'library', resource } as MediaPickerResult);
  }
}
